import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import StateContext from "../StateContext";
import { useTranslation } from "react-i18next";
import { buildTapCardDetails } from "../helper/customeObj";

const TAP_CARD_SDK_URL = "https://tap-sdks.b-cdn.net/card/1.0.2/index.js";
const TAP_CARD_SDK_SCRIPT_ID = "tap-card-sdk-v2-script";

const GoSellElementsTrans = forwardRef((props, ref) => {
  const {t} = useTranslation();
  const appState = useContext(StateContext);
  const [isSdkLoaded, setIsSdkLoaded] = useState(Boolean(window.CardSDK));
  const [sdkInstance, setSdkInstance] = useState(null);
  const cardContainerRef = useRef(null);
  const containerIdRef = useRef(`tap-card-sdk-${Math.random().toString(36).slice(2, 10)}`);
  const latestPropsRef = useRef(props);

  useEffect(() => {
    latestPropsRef.current = props;
  }, [props]);

  const getAmount = () => {
    const value = Number(props.inpValue ?? props.amount);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const getPublicKey = () => {
    const storeTapKey =
      appState.storeInfo?.tapKey ?? appState.storeInfo?.key ?? null;

    if (props.status === "invoice") {
      return props.tapkey || storeTapKey || null;
    }
    if (props.live) {
      return process.env.REACT_APP_TAP_LIVE_KEY_SAVE_CARD || storeTapKey;
    }
    return process.env.REACT_APP_TAP_TEST_KEY_SAVE_CARD || storeTapKey;
  };

  const getCurrencyCode = () => {
    return (props.currency || appState.storeInfo?.storeCurrency?.code || "KWD").toUpperCase();
  };

  useEffect(() => {
    if (window.CardSDK) {
      setIsSdkLoaded(true);
      return undefined;
    }

    const existingScript = document.getElementById(TAP_CARD_SDK_SCRIPT_ID);
    if (existingScript) {
      const onLoad = () => setIsSdkLoaded(true);
      existingScript.addEventListener("load", onLoad);
      return () => existingScript.removeEventListener("load", onLoad);
    }

    const script = document.createElement("script");
    script.id = TAP_CARD_SDK_SCRIPT_ID;
    script.src = TAP_CARD_SDK_URL;
    script.async = true;
    script.onload = () => setIsSdkLoaded(true);
    script.onerror = () => {
      if (props.setButtonLoading) props.setButtonLoading(false);
    };
    document.head.appendChild(script);

    return undefined;
  }, []);

  useEffect(() => {
    if (!isSdkLoaded || !window.CardSDK || !cardContainerRef.current) return undefined;

    const publicKey = getPublicKey();
    if (!publicKey) return undefined;

    const { renderTapCard, Theme, Currencies, Direction, Edges, Locale } = window.CardSDK;
    const currencyCode = getCurrencyCode();
    const resolvedCurrency = Currencies?.[currencyCode] || Currencies?.KWD || "KWD";

    cardContainerRef.current.id = containerIdRef.current;

    let instance = null;
    try {
      instance = renderTapCard(containerIdRef.current, {
        publicKey,
        merchant: {
          id: appState.storeInfo?.merchantId || props.merchantId || "",
        },
        transaction: {
          amount: getAmount(),
          currency: resolvedCurrency,
        },
        acceptance: {
          supportedBrands: ["AMERICAN_EXPRESS", "VISA", "MASTERCARD", "MADA"],
          supportedCards: "ALL",
        },
        fields: {
          cardHolder: true,
        },
        addons: {
          displayPaymentBrands: true,
          loader: true,
          saveCard: props.payWithValue !== "card",
        },
        interface: {
          locale: Locale?.EN || "en",
          theme: Theme?.LIGHT || "light",
          edges: Edges?.CURVED || "curved",
          direction: Direction?.LTR || "ltr",
        },
        onReady: () => {
          if (latestPropsRef.current.setButtonLoading) latestPropsRef.current.setButtonLoading(false);
        },
        onChangeSaveCardLater: (isSaveCardSelected) => {
          if (latestPropsRef.current.setSaveCard) latestPropsRef.current.setSaveCard(isSaveCardSelected);
        },
        onError: (error) => {
          if (latestPropsRef.current.setButtonLoading) latestPropsRef.current.setButtonLoading(false);
          if (latestPropsRef.current.setAlertMessage) {
            latestPropsRef.current.setAlertMessage({
              open: true,
              message: error?.message || "Card tokenization failed. Please check your card details.",
              alertSuccess: false,
            });
          }
        },
        onSuccess: (data) => {
          const tokenId = data?.id || null;
          if (latestPropsRef.current.payWithValue === "card") {
            if (latestPropsRef.current.setTapCardDetails) {
              latestPropsRef.current.setTapCardDetails(buildTapCardDetails(data));
            }
            if (latestPropsRef.current.setTapId && tokenId) {
              latestPropsRef.current.setTapId(tokenId);
            } else if (latestPropsRef.current.setAlertMessage) {
              latestPropsRef.current.setAlertMessage({
                open: true,
                message: "Could not get payment token. Please try again.",
                alertSuccess: false,
              });
            }
          }
          if (latestPropsRef.current.setButtonLoading) latestPropsRef.current.setButtonLoading(false);
        },
      });
      setSdkInstance(instance);
    } catch (_error) {
      if (props.setButtonLoading) props.setButtonLoading(false);
    }

    return () => {
      if (instance && instance.unmount) {
        instance.unmount();
      }
    };
  }, [
    isSdkLoaded,
    props.tapkey,
    props.live,
    props.status,
    props.inpValue,
    props.amount,
    props.currency,
    appState.storeInfo?.tapKey,
    appState.storeInfo?.key,
    appState.storeInfo?.merchantId,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      submitRequest: () => {
        const currentProps = latestPropsRef.current;
        if (currentProps.setButtonLoading) currentProps.setButtonLoading(true);

        if (sdkInstance && sdkInstance.submit) {
          sdkInstance.submit();
          return;
        }

        if (sdkInstance && sdkInstance.tokenize) {
          sdkInstance.tokenize();
          return;
        }

        if (window.CardSDK && typeof window.CardSDK.tokenize === "function") {
          window.CardSDK.tokenize();
          return;
        }

        if (currentProps.setButtonLoading) currentProps.setButtonLoading(false);
        if (currentProps.setAlertMessage) {
          currentProps.setAlertMessage({
            open: true,
            message: "Payment form is not ready. Please wait a moment and try again.",
            alertSuccess: false,
          });
        }
      },
    }),
    [sdkInstance, props.setButtonLoading]
  );

  return (
    <div id="goSellElement" className="App col-12 p-0">
      <div id={containerIdRef.current} ref={cardContainerRef} />
      <p id="msg" />
      {/* <StandardBtn
        onClick={() => console.log('sd')}
        disabled={sdkInstance ? false : true}
        loading={false}
        icon={null}
        loadingText={""}
        defaultText={t("invoice.Confirm-&-Pay").toUpperCase()}
      /> */}
    </div>
  );
});

export default GoSellElementsTrans;
